import { useEffect, useMemo, useState } from "react";
import { Phone, Instagram, Check, User, Link2 } from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import {
  UNASSIGNED_INTEGRATION_FILTER,
  isUnassignedIntegrationFilter,
} from "@/lib/conversation-integration-assign";
import { fetchChannelUnassignedCounts, INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT } from "@/lib/inbox-stats";
import { isUazapiEnabledForWorkspace } from "@/lib/uazapi-feature-flags";
import {
  sortInstagramIntegrations,
  sortMessengerIntegrations,
  sortWhatsAppIntegrations,
} from "@/lib/integration-sort";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import {
  ReconcileOrphanConversationsDialog,
  type ReconcileIntegrationOption,
} from "@/components/conversations/ReconcileOrphanConversationsDialog";


interface Integration {
  id: string;
  provider: string;
  config: Record<string, unknown> | null;
  instance_identifier: string | null;
  instagram_account_id: string | null;
  page_id: string | null;
  status: string | null;
}

interface ChannelToggleProps {
  value: 'whatsapp' | 'instagram' | 'messenger';
  onChange: (value: 'whatsapp' | 'instagram' | 'messenger') => void;
  workspaceId?: string;
  selectedIntegrationId?: string | null;
  onIntegrationSelect?: (integrationId: string | null) => void;
  showMineFilter?: boolean;
  isMineActive?: boolean;
  onMineToggle?: () => void;
  mineCount?: number;
  isOwner?: boolean;
  className?: string;
}

function getConfigString(config: Integration["config"], key: string): string {
  const value = config?.[key];
  return typeof value === "string" ? value : "";
}

function getWhatsAppLabel(integration: Integration): { name: string; detail: string; isCloud: boolean; isNewBaileys: boolean; isLegacyBaileys: boolean } {
  const config = integration.config;
  const isCloud = integration.provider === "whatsapp_cloud";
  const isNewBaileys = integration.provider === "whatsapp_uazapi";
  const isLegacyBaileys = integration.provider === "whatsapp_evolution";
  const name = whatsappIntegrationDisplayLabel(
    {
      instance_identifier: integration.instance_identifier,
      provider: integration.provider,
      config: {
        friendly_name: getConfigString(config, "friendly_name") || null,
        instance_name: getConfigString(config, "instanceName") || getConfigString(config, "instance_name") || null,
        phone_number: getConfigString(config, "phone_number") || getConfigString(config, "phone") || null,
        display_phone_number: getConfigString(config, "display_phone_number") || null,
        verified_name: getConfigString(config, "verified_name") || null,
      },
    },
    "WhatsApp"
  );
  const detail = isCloud
    ? (getConfigString(config, "verified_name") || getConfigString(config, "display_phone_number"))
    : (getConfigString(config, "phone") || getConfigString(config, "number"));
  return { name, detail, isCloud, isNewBaileys, isLegacyBaileys };
}

function getInstagramLabel(integration: Integration): { name: string; detail: string } {
  const config = integration.config;
  const instagramUsername = getConfigString(config, "instagram_username");
  const name = instagramUsername ? `@${instagramUsername}` : getConfigString(config, "page_name") || "Instagram";
  const detail = getConfigString(config, "instagram_name");
  return { name, detail };
}

function getMessengerLabel(integration: Integration): { name: string; detail: string } {
  const config = integration.config;
  const name = getConfigString(config, "page_name") || "Messenger";
  return { name, detail: "" };
}

function appendDisconnectedDetail(detail: string, integration: Integration): string {
  if (integration.status === "disconnected") {
    return detail.trim() ? `${detail} · Desconectado` : "Desconectado";
  }
  return detail;
}

function getChannelFromProvider(provider: string): 'whatsapp' | 'instagram' | 'messenger' | null {
  if (provider === "whatsapp_evolution" || provider === "whatsapp_cloud" || provider === "whatsapp_uazapi") return "whatsapp";
  if (provider === "instagram") return "instagram";
  if (provider === "messenger") return "messenger";
  return null;
}

export function ChannelToggle({ 
  value, onChange, workspaceId, selectedIntegrationId, onIntegrationSelect,
  showMineFilter, isMineActive, onMineToggle, mineCount, className,
}: ChannelToggleProps) {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [instagramOpen, setInstagramOpen] = useState(false);

  const [messengerOpen, setMessengerOpen] = useState(false);
  const [unassignedCounts, setUnassignedCounts] = useState({
    whatsapp: 0,
    instagram: 0,
    messenger: 0,
  });
  const [reconcileChannel, setReconcileChannel] = useState<
    "whatsapp" | "instagram" | "messenger" | null
  >(null);
  const [reconcileBump, setReconcileBump] = useState(0);


  useEffect(() => {
    if (!workspaceId) return;
    const fetchIntegrations = async () => {
      const { data } = await supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, instagram_account_id, page_id, status")
        .eq("workspace_id", workspaceId)
        .in("status", ["active", "pending", "disconnected"])
        .in("provider", ["whatsapp_evolution", "whatsapp_cloud", "whatsapp_uazapi", "instagram", "messenger"]);
      if (data) setIntegrations(data as Integration[]);
    };
    fetchIntegrations();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;

    const refresh = () => {
      void fetchChannelUnassignedCounts(supabase, workspaceId).then(setUnassignedCounts);
    };
    refresh();

    const onInvalidate = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string }>).detail;
      if (detail?.workspaceId && detail.workspaceId !== workspaceId) return;
      refresh();
    };

    window.addEventListener(INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT, onInvalidate);
    return () => window.removeEventListener(INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT, onInvalidate);
  }, [workspaceId, reconcileBump]);

  const whatsappIntegrations = useMemo(
    () =>
      sortWhatsAppIntegrations(
        integrations.filter(
          (i) =>
            i.provider === "whatsapp_evolution" ||
            i.provider === "whatsapp_cloud" ||
            i.provider === "whatsapp_uazapi",
        ),
      ),
    [integrations],
  );
  const instagramIntegrations = useMemo(
    () => sortInstagramIntegrations(integrations.filter((i) => i.provider === "instagram")),
    [integrations],
  );
  const messengerIntegrations = useMemo(
    () => sortMessengerIntegrations(integrations.filter((i) => i.provider === "messenger")),
    [integrations],
  );

  const uazapiAllowed = workspaceId ? isUazapiEnabledForWorkspace(workspaceId) : false;
  const hasUazapiWhatsApp = whatsappIntegrations.some((i) => i.provider === "whatsapp_uazapi");
  const singleWhatsAppAccordionMode = uazapiAllowed && hasUazapiWhatsApp;
  const whatsappIntegrationsForDisplay = singleWhatsAppAccordionMode
    ? whatsappIntegrations.filter((i) => i.provider !== "whatsapp_evolution")
    : whatsappIntegrations;

  useEffect(() => {
    if (!workspaceId || integrations.length === 0) return;

    const currentChannelIntegrations =
      value === "whatsapp"
        ? whatsappIntegrations
        : value === "instagram"
          ? instagramIntegrations
          : messengerIntegrations;

    if (currentChannelIntegrations.length > 0) return;

    const firstAvailable =
      whatsappIntegrations[0] || instagramIntegrations[0] || messengerIntegrations[0];
    const nextChannel = firstAvailable ? getChannelFromProvider(firstAvailable.provider) : null;

    if (nextChannel) {
      onChange(nextChannel);
      onIntegrationSelect?.(firstAvailable.id);
    }
  }, [
    workspaceId,
    integrations.length,
    value,
    whatsappIntegrations,
    instagramIntegrations,
    messengerIntegrations,
    onChange,
    onIntegrationSelect,
  ]);

  const handleWhatsappClick = () => {
    onChange('whatsapp');
    onIntegrationSelect?.(whatsappIntegrations[0]?.id || null);
    setInstagramOpen(false);
    setMessengerOpen(false);
  };

  const handleInstagramClick = () => {
    onChange('instagram');
    onIntegrationSelect?.(instagramIntegrations[0]?.id || null);
    setWhatsappOpen(false);
    setMessengerOpen(false);
  };

  const handleMessengerClick = () => {
    onChange('messenger');
    onIntegrationSelect?.(messengerIntegrations[0]?.id || null);
    setWhatsappOpen(false);
    setInstagramOpen(false);
  };

  const renderUnassignedOption = (
    channel: "whatsapp" | "instagram" | "messenger",
    count: number,
    onClose: () => void,
    icon: React.ReactNode,
    activeClass: string,
    idleClass: string,
  ) => {
    if (count <= 0) return null;
    const isSelected =
      value === channel && isUnassignedIntegrationFilter(selectedIntegrationId);
    return (
      <button
        type="button"
        onClick={() => {
          onChange(channel);
          onIntegrationSelect?.(UNASSIGNED_INTEGRATION_FILTER);
          onClose();
        }}
        className={cn(
          "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors",
          isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted",
        )}
      >
        <div
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
            isSelected ? activeClass : idleClass,
          )}
        >
          {isSelected ? <Check className="w-3.5 h-3.5" /> : icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">Em aberto</p>
          <p className="text-xs text-muted-foreground truncate">
            {count} conversa{count === 1 ? "" : "s"} sem conexão fixa
          </p>
        </div>
      </button>
    );
  };

  const renderReconcileButton = (
    channel: "whatsapp" | "instagram" | "messenger",
    list: Integration[],
    unassignedCount: number,
    onClose: () => void,
  ) => {
    const hasUazapi = channel === "whatsapp" && list.some((i) => i.provider === "whatsapp_uazapi");
    const uazapiMigrationMode =
      channel === "whatsapp" && uazapiAllowed && hasUazapi;
    const hasLegacyEvolution =
      uazapiMigrationMode && list.some((i) => i.provider === "whatsapp_evolution");
    const hasDisconnected = list.some((i) => i.status === "disconnected");
    const hasActive = list.some((i) => i.status === "active");
    const hasReconcileableSources =
      unassignedCount > 0 || hasLegacyEvolution || hasDisconnected;
    const hasTarget =
      hasActive ||
      (uazapiMigrationMode && hasUazapi);
    if (!hasReconcileableSources || !hasTarget) return null;
    return (
      <>
        <div className="my-1 border-t" />
        <button
          type="button"
          onClick={() => {
            onClose();
            setReconcileChannel(channel);
          }}
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm font-medium text-primary hover:bg-primary/5"
        >
          <Link2 className="w-4 h-4" />
          Reconciliar conversas
        </button>
      </>
    );
  };


  const renderWhatsAppButton = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-full w-8 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150",
        value === 'whatsapp' && !isMineActive
          ? "bg-green-500 text-white"
          : "text-muted-foreground hover:text-green-600 hover:bg-green-500/10"
      )}
    >
      <Phone className="w-4 h-4" />
    </button>
  );

  const renderInstagramButton = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-full w-8 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150",
        value === 'instagram' && !isMineActive
          ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white"
          : "text-muted-foreground hover:text-pink-500 hover:bg-pink-500/10"
      )}
    >
      <Instagram className="w-4 h-4" />
    </button>
  );

  return (
    <div className={cn("inline-flex h-9 items-stretch gap-0.5 rounded-lg bg-muted/50 p-0.5", className)}>
      {/* WhatsApp */}
      {whatsappIntegrations.length > 0 && (whatsappIntegrationsForDisplay.length >= 2 || unassignedCounts.whatsapp > 0 || (singleWhatsAppAccordionMode && whatsappIntegrations.some((i) => i.provider === "whatsapp_evolution")) ? (
        <Popover open={whatsappOpen} onOpenChange={setWhatsappOpen}>
          <PopoverTrigger asChild>
            {renderWhatsAppButton()}
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" sideOffset={8}>
            <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Selecionar número</p>
            <div className="space-y-0.5">
              {whatsappIntegrationsForDisplay.map((integ) => {
                const { name, detail: baseDetail, isCloud, isNewBaileys, isLegacyBaileys } = getWhatsAppLabel(integ);
                const detail = appendDisconnectedDetail(baseDetail, integ);
                const isSelected = selectedIntegrationId === integ.id && value === 'whatsapp';
                return (
                  <button
                    key={integ.id}
                    onClick={() => {
                      onChange('whatsapp');
                      onIntegrationSelect?.(integ.id);
                      setWhatsappOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors",
                      isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                      isSelected ? "bg-green-500 text-white" : "bg-green-500/10 text-green-600"
                    )}>
                      {isSelected ? <Check className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{name}</p>
                        {isCloud && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/15 text-primary shrink-0">
                            Cloud
                          </span>
                        )}
                        {!singleWhatsAppAccordionMode && isNewBaileys && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 shrink-0">
                            Novo
                          </span>
                        )}
                        {!singleWhatsAppAccordionMode && isLegacyBaileys && whatsappIntegrations.some((w) => w.provider === "whatsapp_uazapi") && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                            Legado
                          </span>
                        )}
                      </div>
                      {detail ? <p className="text-xs text-muted-foreground truncate">{detail}</p> : null}
                    </div>
                  </button>
                );
              })}
              {renderUnassignedOption(
                "whatsapp",
                unassignedCounts.whatsapp,
                () => setWhatsappOpen(false),
                <Phone className="w-3.5 h-3.5" />,
                "bg-green-500 text-white",
                "bg-muted text-muted-foreground",
              )}
              {renderReconcileButton(
                "whatsapp",
                whatsappIntegrations,
                unassignedCounts.whatsapp,
                () => setWhatsappOpen(false),
              )}

            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>{renderWhatsAppButton(handleWhatsappClick)}</TooltipTrigger>
          <TooltipContent side="bottom">WhatsApp</TooltipContent>
        </Tooltip>
      ))}

      {/* Instagram */}
      {instagramIntegrations.length > 0 && (instagramIntegrations.length >= 2 || unassignedCounts.instagram > 0 ? (
        <Popover open={instagramOpen} onOpenChange={setInstagramOpen}>
          <PopoverTrigger asChild>
            {renderInstagramButton()}
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" sideOffset={8}>
            <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Selecionar conta</p>
            <div className="space-y-0.5">
              {instagramIntegrations.map((integ) => {
                const { name, detail: baseDetail } = getInstagramLabel(integ);
                const detail = appendDisconnectedDetail(baseDetail, integ);
                const isSelected = selectedIntegrationId === integ.id && value === 'instagram';
                return (
                  <button
                    key={integ.id}
                    onClick={() => {
                      onChange('instagram');
                      onIntegrationSelect?.(integ.id);
                      setInstagramOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors",
                      isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                      isSelected ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white" : "bg-pink-500/10 text-pink-600"
                    )}>
                      {isSelected ? <Check className="w-3.5 h-3.5" /> : <Instagram className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{name}</p>
                      {detail ? <p className="text-xs text-muted-foreground truncate">{detail}</p> : null}
                    </div>
                  </button>
                );
              })}
              {renderUnassignedOption(
                "instagram",
                unassignedCounts.instagram,
                () => setInstagramOpen(false),
                <Instagram className="w-3.5 h-3.5" />,
                "bg-gradient-to-r from-pink-500 to-rose-500 text-white",
                "bg-pink-500/10 text-pink-600",
              )}
              {renderReconcileButton(
                "instagram",
                instagramIntegrations,
                unassignedCounts.instagram,
                () => setInstagramOpen(false),
              )}

            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>{renderInstagramButton(handleInstagramClick)}</TooltipTrigger>
          <TooltipContent side="bottom">Instagram</TooltipContent>
        </Tooltip>
      ))}

      {/* Messenger */}
      {messengerIntegrations.length > 0 && (messengerIntegrations.length >= 2 || unassignedCounts.messenger > 0 ? (
        <Popover open={messengerOpen} onOpenChange={setMessengerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "h-full w-8 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150",
                value === 'messenger' && !isMineActive
                  ? "bg-blue-500 text-white"
                  : "text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10"
              )}
            >
              <MessengerIcon className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" sideOffset={8}>
            <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Selecionar página</p>
            <div className="space-y-0.5">
              {messengerIntegrations.map((integ) => {
                const { name, detail: baseDetail } = getMessengerLabel(integ);
                const detail = appendDisconnectedDetail(baseDetail, integ);
                const isSelected = selectedIntegrationId === integ.id && value === 'messenger';
                return (
                  <button
                    key={integ.id}
                    onClick={() => {
                      onChange('messenger');
                      onIntegrationSelect?.(integ.id);
                      setMessengerOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors",
                      isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                      isSelected ? "bg-blue-500 text-white" : "bg-blue-500/10 text-blue-600"
                    )}>
                      {isSelected ? <Check className="w-3.5 h-3.5" /> : <MessengerIcon className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{name}</p>
                      {detail ? <p className="text-xs text-muted-foreground truncate">{detail}</p> : null}
                    </div>
                  </button>
                );
              })}
              {renderUnassignedOption(
                "messenger",
                unassignedCounts.messenger,
                () => setMessengerOpen(false),
                <MessengerIcon className="w-3.5 h-3.5" />,
                "bg-blue-500 text-white",
                "bg-blue-500/10 text-blue-600",
              )}
              {renderReconcileButton(
                "messenger",
                messengerIntegrations,
                unassignedCounts.messenger,
                () => setMessengerOpen(false),
              )}

            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleMessengerClick}
              className={cn(
                "h-full w-8 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150",
                value === 'messenger' && !isMineActive
                  ? "bg-blue-500 text-white"
                  : "text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10"
              )}
            >
              <MessengerIcon className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Messenger</TooltipContent>
        </Tooltip>
      ))}

      {/* Mine filter */}
      {showMineFilter && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onMineToggle}
              className={cn(
                "relative h-full w-8 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150",
                isMineActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/10"
              )}
            >
              <User className="w-4 h-4" />
              {(mineCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                  {mineCount}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Minhas Conversas</TooltipContent>
        </Tooltip>
      )}
    {reconcileChannel && workspaceId && (() => {
      const list =
        reconcileChannel === "whatsapp"
          ? whatsappIntegrations
          : reconcileChannel === "instagram"
            ? instagramIntegrations
            : messengerIntegrations;
      const hasUazapi = list.some((i) => i.provider === "whatsapp_uazapi");
      const uazapiMigrationMode =
        reconcileChannel === "whatsapp" && uazapiAllowed && hasUazapi;
      const preferredTargetId =
        list.find((i) => i.provider === "whatsapp_uazapi")?.id ??
        list.find((i) => i.status === "active")?.id ??
        null;
      const options: ReconcileIntegrationOption[] = list.map((i) => {
        const labelData =
          reconcileChannel === "whatsapp"
            ? getWhatsAppLabel(i)
            : reconcileChannel === "instagram"
              ? getInstagramLabel(i)
              : getMessengerLabel(i);
        const isLegacyBaileys =
          reconcileChannel === "whatsapp" &&
          i.provider === "whatsapp_evolution" &&
          hasUazapi;
        const canBeTarget =
          i.status === "active" ||
          (uazapiMigrationMode && i.provider === "whatsapp_uazapi");
        return {
          id: i.id,
          label: labelData.name,
          detail: labelData.detail,
          status: i.status,
          canBeTarget,
          isLegacy: isLegacyBaileys,
        };
      });
      const channelLabel =
        reconcileChannel === "whatsapp"
          ? "do WhatsApp"
          : reconcileChannel === "instagram"
            ? "do Instagram"
            : "do Messenger";
      return (
        <ReconcileOrphanConversationsDialog
          open={true}
          onOpenChange={(o) => !o && setReconcileChannel(null)}
          workspaceId={workspaceId}
          channel={reconcileChannel}
          channelLabel={channelLabel}
          integrations={options}
          defaultIntegrationId={
            preferredTargetId ??
            (selectedIntegrationId && !isUnassignedIntegrationFilter(selectedIntegrationId)
              ? selectedIntegrationId
              : null)
          }
          onDone={() => setReconcileBump((n) => n + 1)}
        />
      );
    })()}
    </div>
  );
}

